import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import DesktopBudgetHeader from "../components/DesktopBudgetHeader";
import { AppLoader, ButtonLoader } from "../components/Loaders";
import { BUDGET_MODULE_PATHS } from "../constants/budgetModule";
import {
  getMappingState,
  getReferenceData,
  getWorkspace,
  saveExcelToViMapping,
  saveWorkspace,
} from "../api/budgetPortal";
import { useAuth } from "../state/AuthContext";
import {
  getSelectedBudgetModuleYear,
  saveSelectedBudgetModulePage,
  setBudgetFlowCompleted,
  setBudgetFlowStarted,
} from "../utils/budgetFlow";
import {
  applyAutoMatchToImportedRows,
  applyExcelToViMappingToImportedRows,
  buildAccountingMatchMap,
  buildExcelToViMappingPatch,
  buildMappedBudgetRows,
  buildViOptions,
} from "../utils/budgetWorkspace";

const BudgetModuleMappingPage = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [selectedYear] = useState(getSelectedBudgetModuleYear());
  const [workspaceData, setWorkspaceData] = useState(null);
  const [mappedRowsDraft, setMappedRowsDraft] = useState([]);
  const [viOptions, setViOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const showImportedActualPriorYear = useMemo(() => {
    const mappedColumn =
      workspaceData?.workspace?.bootstrap_metadata?.import_session?.mapping
        ?.actualPriorYear;

    if (mappedColumn) {
      return true;
    }

    return mappedRowsDraft.some((row) => Number(row?.actualPriorYear) !== 0);
  }, [mappedRowsDraft, workspaceData]);

  useEffect(() => {
    if (!selectedYear) {
      navigate(BUDGET_MODULE_PATHS.import, { replace: true });
      return;
    }

    const loadData = async () => {
      setLoading(true);
      setError("");

      try {
        const [workspaceResponse, referenceResponse, mappingStateResponse] =
          await Promise.all([
            getWorkspace(token, selectedYear),
            getReferenceData(token),
            getMappingState(token),
          ]);

        const workspace = workspaceResponse?.data ?? null;
        const importedRows =
          workspace?.workspace?.bootstrap_metadata?.import_session?.imported_rows ?? [];

        if (!importedRows.length) {
          navigate(BUDGET_MODULE_PATHS.import, { replace: true });
          return;
        }

        const nextViOptions = buildViOptions(referenceResponse?.data?.vicode_list ?? []);

        setWorkspaceData(workspace);
        setViOptions(nextViOptions);
        const accountingMatchedRows = applyAutoMatchToImportedRows(
          importedRows,
          buildAccountingMatchMap(
            workspace?.accounting_snapshot?.year_financials ?? [],
          ),
        );

        setMappedRowsDraft(
          applyExcelToViMappingToImportedRows(
            accountingMatchedRows,
            mappingStateResponse?.data?.excel_to_vi_description_mapping ?? {},
          ),
        );
        setBudgetFlowStarted();
      } catch (loadError) {
        setError(
          loadError?.response?.data?.message ||
          loadError.message ||
          "Unable to load budget mapping data.",
        );
      }

      setLoading(false);
    };

    loadData();
  }, [navigate, selectedYear, token]);

  const persistMappingPatch = async (rows) => {
    const mappingPatch = buildExcelToViMappingPatch(rows);
    if (!Object.keys(mappingPatch).length) {
      return;
    }

    try {
      await saveExcelToViMapping(token, mappingPatch);
    } catch (_error) {
      // Mapping persistence should not interrupt row editing.
    }
  };

  const handleMappedRowChange = (rowId, nextViCode) => {
    setMappedRowsDraft((currentValue) => {
      const nextRows = currentValue.map((row) => {
        if (row.id !== rowId) {
          return row;
        }

        const selectedOption = viOptions.find((option) => option.value === nextViCode);
        return {
          ...row,
          viCode: nextViCode,
          viDescription: selectedOption?.name ?? "",
        };
      });

      persistMappingPatch(nextRows);
      return nextRows;
    });
  };

  const handleSaveMappedRows = async () => {
    const unmappedRows = mappedRowsDraft.filter((row) => !row.viCode);
    if (unmappedRows.length > 0) {
      setError(
        `Map all imported rows before saving. ${unmappedRows.length} rows still need a VI code.`,
      );
      return;
    }

    setSaving(true);
    setError("");

    try {
      const finalBudgetRows = buildMappedBudgetRows(mappedRowsDraft, viOptions);
      const workspace = workspaceData?.workspace ?? {};
      await persistMappingPatch(mappedRowsDraft);

      await saveWorkspace(token, selectedYear, {
        budget_rows: finalBudgetRows,
        planner_details: workspace.planner_details ?? null,
        department_details: workspace.department_details ?? null,
        bootstrap_metadata: {
          ...(workspace.bootstrap_metadata ?? {}),
          import_session: {
            ...(workspace.bootstrap_metadata?.import_session ?? {}),
            imported_rows: mappedRowsDraft,
            status: "mapped",
            mapped_at: new Date().toISOString(),
          },
        },
      });

      setBudgetFlowCompleted();
      saveSelectedBudgetModulePage(BUDGET_MODULE_PATHS.dashboard);
      navigate(BUDGET_MODULE_PATHS.dashboard);
    } catch (saveError) {
      setError(
        saveError?.response?.data?.message ||
        saveError.message ||
        "Unable to save mapped budget rows.",
      );
    }

    setSaving(false);
  };

  return (
    <section id="dashboard-layout-id" className="dashboard-layout">
      <DesktopBudgetHeader showSidebar={false} />
      <div className="dashboard-previousBtn">
        <button
          type="button"
          className="landingPage-discardBtn"
          onClick={() =>
            navigate(BUDGET_MODULE_PATHS.import, {
              state: { skipDraftRedirect: true },
            })
          }
        >
          Previous
        </button>
      </div>

      <div className="dashboard-container-dataTable">
        <div className="dashboard-dataTable">
          <div className="table-section-layout budgetMapping-webTableLayout">
            <div className="budgetMapping-headerRow">
              <div className="table-top-title">Budget mapping for {selectedYear}</div>
              {!loading ? (
                <div className="panel__actions budgetMapping-actions budgetMapping-actionsTop">
                  <button
                    type="button"
                    onClick={handleSaveMappedRows}
                    disabled={saving || loading}
                  >
                    {saving ? <ButtonLoader label="Saving..." /> : "Save Mapping"}
                  </button>
                </div>
              ) : null}
            </div>
            {error ? <div className="web-inlineError">{error}</div> : null}
            {loading ? (
              <AppLoader label="Loading mapping..." />
            ) : (
              <>
                <div className="all-table-container budgetMapping-tableContainer">
                  <div className="all-table">
                    <div className="all-table-row all-table-header">
                      <div className="all-table-cell">VI Description</div>
                      <div className="all-table-cell">Code Description</div>
                      {showImportedActualPriorYear ? (
                        <div className="all-table-cell">Actual Prior Year</div>
                      ) : null}
                    </div>

                    {mappedRowsDraft.map((row) => (
                      <div className="all-table-row" key={row.id}>
                        <div className="all-table-cell">
                          <select
                            className="budgetMapping-select"
                            value={row.viCode}
                            onChange={(event) =>
                              handleMappedRowChange(row.id, event.target.value)
                            }
                          >
                            <option value="">Select VI Description</option>
                            {viOptions.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.name}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="all-table-cell">{row.codeDescription}</div>
                        {showImportedActualPriorYear ? (
                          <div className="all-table-cell">{row.actualPriorYear}</div>
                        ) : null}
                      </div>
                    ))}
                  </div>
                </div>

              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default BudgetModuleMappingPage;
